/** Coerce API permission values (boolean, 0/1, "true"/"false") to boolean. */
export function permissionGranted(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const s = String(value).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  return Boolean(value);
}

/** Backend sometimes uses a different key than the UI module name. */
export const PERMISSION_KEY_ALIASES = {
  nature_of_complaint: 'issue_types',
  nature_of_complaints: 'issue_types',
  outsource_employees: 'outsource_employee',
  product_categories: 'product_category',
  leave: 'leave_management',
  leaves: 'leave_management',
  leave_managements: 'leave_management',
  leave_requests: 'leave_management',
};

export function canonicalPermissionKey(key) {
  const k = String(key || '');
  return PERMISSION_KEY_ALIASES[k] || k;
}

export function permissionGrantedForKey(permissions, key) {
  if (!key) return false;
  if (permissionGranted(permissions?.[key])) return true;
  const canonical = canonicalPermissionKey(key);
  if (canonical !== key && permissionGranted(permissions?.[canonical])) return true;
  return Object.entries(PERMISSION_KEY_ALIASES).some(
    ([alias, target]) =>
      target === canonical && permissionGranted(permissions?.[alias])
  );
}

function menuModuleRecord(menuPermissions, key) {
  if (!key || !menuPermissions || typeof menuPermissions !== 'object') return null;
  const canonical = canonicalPermissionKey(key);
  const direct = menuPermissions[canonical] || menuPermissions[key];
  if (direct && typeof direct === 'object') return direct;
  for (const [alias, target] of Object.entries(PERMISSION_KEY_ALIASES)) {
    if (target !== canonical) continue;
    const rec = menuPermissions[alias];
    if (rec && typeof rec === 'object') return rec;
  }
  return null;
}

/** Sidebar/module access: `menu_permissions[key].enabled` is the source of truth. */
export function menuModuleEnabled(menuPermissions, key) {
  const rec = menuModuleRecord(menuPermissions, key);
  if (!rec) return null;
  if (rec.enabled !== undefined) return permissionGranted(rec.enabled);
  if (rec.view !== undefined) return permissionGranted(rec.view);
  return MENU_ACTIONS.some((action) => permissionGranted(rec[action]));
}

export function moduleAccessGranted(menuPermissions, permissions, key) {
  const fromMenu = menuModuleEnabled(menuPermissions, key);
  if (fromMenu != null) return fromMenu;
  return permissionGrantedForKey(permissions, key);
}

/** Login `permissions` object → `{ dashboard: true, complaints: false, ... }` */
export function normalizePermissions(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = canonicalPermissionKey(k);
    out[key] = permissionGranted(out[key]) || permissionGranted(v);
  }
  return out;
}

function accountFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const account =
    payload?.data != null && typeof payload.data === 'object' && !Array.isArray(payload.data)
      ? payload.data
      : payload;
  const nested =
    account?.data != null && typeof account.data === 'object' ? account.data : null;
  return nested || account;
}

export const MENU_ACTIONS = ['view', 'edit', 'create', 'delete'];

/**
 * Derive module-level flags from normalized menu_permissions.
 * Used when login/profile no longer returns a top-level `permissions` object.
 */
export function modulePermissionsFromMenu(menuPermissions) {
  const out = {};
  if (!menuPermissions || typeof menuPermissions !== 'object') return out;
  for (const [moduleKey, actions] of Object.entries(menuPermissions)) {
    if (!actions || typeof actions !== 'object' || Array.isArray(actions)) continue;
    if (actions.enabled !== undefined) {
      out[moduleKey] = permissionGranted(actions.enabled);
      continue;
    }
    if (actions.view !== undefined) {
      out[moduleKey] = permissionGranted(actions.view);
      continue;
    }
    out[moduleKey] = MENU_ACTIONS.some((action) => permissionGranted(actions[action]));
  }
  return out;
}

/**
 * Merge explicit module permissions with flags derived from menu_permissions.
 * `menu_permissions.enabled` wins when the module is present in the menu map.
 */
export function mergeModulePermissions(explicitPermissions, menuPermissions) {
  return {
    ...normalizePermissions(explicitPermissions),
    ...modulePermissionsFromMenu(menuPermissions),
  };
}

/** Pull permissions from login / account payloads (tolerant to nesting). */
export function permissionsFromAccountPayload(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const account = accountFromPayload(payload);
  const raw =
    account?.permissions ??
    payload?.permissions ??
    {};
  const menu = menuPermissionsFromAccountPayload(payload);
  return mergeModulePermissions(raw, menu);
}
export function normalizeMenuPermissions(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [moduleKey, value] of Object.entries(raw)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;

    const hasNestedActions =
      value.actions != null &&
      typeof value.actions === 'object' &&
      !Array.isArray(value.actions);
    const actionsSource = hasNestedActions ? value.actions : value;

    const moduleOut = {};
    for (const action of MENU_ACTIONS) {
      if (actionsSource[action] !== undefined) {
        moduleOut[action] = permissionGranted(actionsSource[action]);
      }
    }

    if (value.enabled !== undefined) {
      const enabled = permissionGranted(value.enabled);
      moduleOut.enabled = enabled;
      if (!enabled) {
        for (const action of MENU_ACTIONS) {
          if (moduleOut[action] !== undefined) moduleOut[action] = false;
        }
      }
    }

    if (!Object.keys(moduleOut).length) continue;

    const key = canonicalPermissionKey(moduleKey);
    const existing = out[key];
    if (!existing) {
      out[key] = moduleOut;
      continue;
    }

    const merged = { ...existing };
    for (const action of [...MENU_ACTIONS, 'enabled']) {
      if (existing[action] !== undefined || moduleOut[action] !== undefined) {
        merged[action] = permissionGranted(existing[action]) || permissionGranted(moduleOut[action]);
      }
    }
    out[key] = merged;
  }
  return out;
}

/** Pull menu_permissions from login / account payloads (tolerant to nesting). */
export function menuPermissionsFromAccountPayload(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const account = accountFromPayload(payload);
  const raw =
    account?.menu_permissions ??
    account?.menuPermissions ??
    payload?.menu_permissions ??
    payload?.menuPermissions ??
    {};
  return normalizeMenuPermissions(raw);
}

/**
 * Check a module action against menu_permissions.
 * When menu_permissions is absent (legacy sessions), fall back to module-level permission.
 */
export function menuActionGranted(menuPermissions, moduleKey, action, modulePermissionFallback) {
  if (!moduleKey || !action) return false;
  const normalizedAction = String(action).toLowerCase();
  const modulePerms = menuModuleRecord(menuPermissions, moduleKey);
  if (modulePerms && typeof modulePerms === 'object') {
    if (modulePerms.enabled === false) return false;
    if (normalizedAction in modulePerms) {
      return permissionGranted(modulePerms[normalizedAction]);
    }
  }
  if (!menuPermissions || Object.keys(menuPermissions).length === 0) {
    return permissionGranted(modulePermissionFallback);
  }
  return false;
}

export function mergePermissionMaps(base = {}, incoming = {}) {
  if (!incoming || typeof incoming !== 'object') return base || {};
  if (!base || typeof base !== 'object' || !Object.keys(incoming).length) {
    return Object.keys(incoming).length ? incoming : base || {};
  }
  return { ...base, ...incoming };
}

export function payloadHasMenuPermissions(payload) {
  const account = accountFromPayload(payload);
  const raw =
    account?.menu_permissions ??
    account?.menuPermissions ??
    payload?.menu_permissions ??
    payload?.menuPermissions;
  return Boolean(raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length);
}
